import { EventEmitter } from "events";
import { queryParamSerializer } from "@shared/utils/url";
import {
  isIframeMessage as isIframeUtilMessage,
  setupIframeHeightListener,
} from "@shared/utils/iframe-utils/iframe-resize";
import { WidgetDatalessEvents, WidgetQueryParams } from "./Widget-types";

const bodyClass = "showpass-widget-body";
const widgetID = "showpass-widget";
const iFrameClass = "showpass-widget-iframe";
const animationDuration = 300;

interface CloseOptions {
  removeBodyClass?: boolean;
  remount?: boolean;
}

interface BackToCalendarEvent {
  venueId: string;
  params: WidgetQueryParams;
}

interface CalendarEventPurchaseEvent {
  slug: string;
  params: WidgetQueryParams;
  containerId: string;
}

interface BackToEventEvent {
  slug: string;
  params: WidgetQueryParams;
}

type CallbackWithOptionalArgs<T> = ((args: T) => void) | (() => void);

export interface Widget extends EventEmitter {
  on(event: WidgetDatalessEvents, callback: () => void): this;
  on(
    event: "showpass.event-purchase.back-to-calendar",
    callback: CallbackWithOptionalArgs<BackToCalendarEvent>
  ): this;
  on(
    event: "showpass.calendar.event-purchase",
    callback: CallbackWithOptionalArgs<CalendarEventPurchaseEvent>
  ): this;
  on(
    event: "showpass.checkout.back-to-event",
    callback: CallbackWithOptionalArgs<BackToEventEvent>
  ): this;
}

export interface WidgetOptions {
  src: string;
  containerId?: string;
  queryParams?: Record<string, unknown>;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

export class Widget extends EventEmitter {
  isModalOpen = false;
  isEmbedded = false;

  iframeContainer: HTMLDivElement;

  iFrame: HTMLIFrameElement;

  scrollTop: number;

  cleanupHeightListener: () => void = noop;

  constructor({ src, containerId, queryParams }: WidgetOptions) {
    super();

    if (containerId) {
      this.iframeContainer = document.getElementById(
        containerId
      ) as HTMLDivElement;

      if (!this.iframeContainer) {
        throw new Error(
          `Element with id ${containerId} was not found. A valid element id must be provided to embed a widget`
        );
      }

      this.iframeContainer.classList.add("showpass-widget-embedded-wrapper");
      this.iframeContainer.classList.add("showpass-is-loading");
      this.isEmbedded = true;
    } else {
      this.iframeContainer = document.createElement("div");
      this.iframeContainer.classList.add("showpass-widget-wrapper");
      this.iframeContainer.classList.add("showpass-is-loading");
    }

    this.iFrame = document.createElement("iframe");
    this.iFrame.setAttribute("id", widgetID);
    this.iFrame.setAttribute("allow", "payment");
    this.iFrame.classList.add(iFrameClass);

    this.iFrame.src = `${src}?${queryParamSerializer({
      ...queryParams,
      isEmbedded: this.isEmbedded,
    })}`;

    this.iFrame.addEventListener("load", () => {
      this.iframeContainer.classList.remove("showpass-is-loading");
    });
  }

  private mount = () => {
    if (this.isEmbedded) {
      this.iframeContainer.appendChild(this.iFrame);

      // Set up iframe height listener with wheel event handling
      this.cleanupHeightListener = setupIframeHeightListener(this.iFrame, {
        enableScrollEvents: true,
      });
    }
  };

  unmount = () => {
    if (this.isEmbedded) {
      this.iframeContainer.removeChild(this.iFrame);
    }

    if (this.cleanupHeightListener) {
      this.cleanupHeightListener();
    }
  };

  remount = () => {
    this.unmount();
    this.mount();
  };

  show = () => {
    this[this.isEmbedded ? "mount" : "open"]();

    window.addEventListener("message", this.handleMessage);

    return this;
  };

  private open = () => {
    this.isModalOpen = true;
    this.iframeContainer.appendChild(this.iFrame);
    this.scrollTop = window.pageYOffset;

    document.body.appendChild(this.iframeContainer);
    // Animation starts automatically due to CSS
    document.documentElement.classList.add(bodyClass);
    document.body.style.top = `${-this.scrollTop}px`;

    return this;
  };

  private handleMessage = (event: MessageEvent) => {
    if (isIframeUtilMessage(event)) return;

    switch (event.data) {
      case "showpass.widget.close":
      case "showpass.calendar.close":
      case "showpass.checkout.close":
      case "showpass.security.close":
      case "showpass.product-purchase.close":
      case "showpass.shopping-cart.close":
        this.emit(event.data as WidgetDatalessEvents);
        this.close();
        break;
      default:
        if (event.data?.id) {
          this.emit(event.data.id, event.data);
        } else {
          this.emit(event.data as WidgetDatalessEvents);
        }
        break;
    }
  };

  close = ({ removeBodyClass = true, remount = true }: CloseOptions = {}) => {
    if (this.isEmbedded && remount) {
      this.remount();
    }

    // this'll also catch when we're embedded since this.isVisible is only set when the modal opens
    if (!this.isModalOpen) {
      return;
    }

    this.isModalOpen = false;
    this.removeAllListeners();
    window.removeEventListener("message", this.handleMessage);

    if (this.cleanupHeightListener) {
      this.cleanupHeightListener();
    }

    // Add closing class and wait for animation
    this.iframeContainer.classList.add("closing");

    setTimeout(() => {
      document.body.removeChild(this.iframeContainer);

      if (removeBodyClass) {
        document.documentElement.classList.remove(bodyClass);
      }
      document.body.style.top = "auto";
      window.scrollTo(0, this.scrollTop);
    }, animationDuration - 1);
  };
}
